
import { Lead, Form } from "@/types";

// Function to extract investment range from lead data
export const getInvestmentRange = (lead: Lead): string => {
  if (!lead || !lead.data) return 'Not specified';
  
  // Try to get the investment amount from various possible field names
  const investmentAmount = lead.data.investment_amount 
    || lead.data.investmentAmount 
    || lead.data.investment_range
    || lead.data.investmentRange;
    
  return investmentAmount || 'Not specified';
};

// Function to extract investment experience from lead data
export const getInvestmentExperience = (lead: Lead): string => {
  if (!lead || !lead.data) return 'Not specified';
  
  // Try to get the investment experience from various possible field names
  const experience = lead.data.investment_experience 
    || lead.data.investmentExperience 
    || lead.data.experience;
    
  return experience || 'Not specified';
};

// Function to get experience display string with context from form
export const getExperienceDisplayFromForm = (lead: Lead, forms: Form[]): string => {
  if (!lead || !lead.data) return 'Not specified';
  
  // Find the form associated with this lead
  const form = forms.find(f => f.id === lead.formId);
  if (!form) return getInvestmentExperience(lead);
  
  const exp = getInvestmentExperience(lead);
  
  // Check if the experience is "yes" or "no" and add context
  if (exp.toLowerCase() === 'yes') {
    return 'Experienced investor';
  } else if (exp.toLowerCase() === 'no') {
    return 'First-time investor';
  }
  
  return exp;
};

// Function to extract investment timeline from lead data
export const getInvestmentTimeline = (lead: Lead): string => {
  if (!lead || !lead.data) return 'Not specified';
  
  // Try to get the investment timeline from various possible field names
  const timeline = lead.data.investment_timeline 
    || lead.data.investmentTimeline 
    || lead.data.timeline;
    
  return timeline || 'Not specified';
};

// Function to determine lead quality from various factors
export const determineLead = (lead: Lead): { quality: string; reason: string } => {
  if (!lead || !lead.data) {
    return { quality: 'low', reason: 'Incomplete data' };
  }
  
  const investmentAmount = getInvestmentRange(lead);
  const timeline = getInvestmentTimeline(lead);
  
  // Higher investment amounts suggest higher quality leads
  if (
    investmentAmount.includes('$50,000') || 
    investmentAmount.includes('$100,000') ||
    investmentAmount.includes('100k') ||
    investmentAmount.includes('1M')
  ) {
    return { 
      quality: 'high', 
      reason: 'High investment amount'
    };
  }
  
  // Leads with immediate investment timelines are higher quality
  if (
    timeline.includes('immediately') || 
    timeline.includes('within a week') ||
    timeline.includes('ASAP') 
  ) {
    return { 
      quality: 'high', 
      reason: 'Urgent investment timeline'
    };
  }
  
  // Medium quality leads
  if (
    investmentAmount.includes('$25,000') || 
    timeline.includes('within a month')
  ) {
    return { 
      quality: 'medium', 
      reason: 'Moderate investment or timeline'
    };
  }
  
  // Default to low quality if no specific indicators
  return { 
    quality: 'low', 
    reason: 'No strong investment indicators'
  };
};

// Calculate estimated close percentage based on lead quality
export const calculateEstimatedClosePercentage = (lead: Lead): number => {
  const { quality } = determineLead(lead);
  
  switch (quality) {
    case 'high':
      return 80;
    case 'medium':
      return 50;
    case 'low':
      return 20;
    default:
      return 30;
  }
};

// Function to extract full name from lead data
export const getFullName = (lead: Lead): string => {
  if (!lead || !lead.data) return 'Unknown';
  
  const firstName = lead.data.first_name || lead.data.firstName || '';
  const lastName = lead.data.last_name || lead.data.lastName || '';
  
  if (firstName || lastName) {
    return `${firstName} ${lastName}`.trim();
  }
  
  // Try to get a name field directly
  const name = lead.data.name || lead.data.fullName || lead.data.full_name;
  
  if (name && typeof name === 'string') {
    return name;
  }
  
  return 'Unknown';
};

// Function to extract email from lead data
export const getEmail = (lead: Lead): string => {
  if (!lead || !lead.data) return '';
  
  return lead.data.email || lead.data.emailAddress || '';
};

// Function to extract phone from lead data
export const getPhone = (lead: Lead): string => {
  if (!lead || !lead.data) return '';
  
  return lead.data.phone || lead.data.phoneNumber || lead.data.phone_number || '';
};

// Function to extract location (state/city) from lead data
export const getLocation = (lead: Lead): string => {
  if (!lead || !lead.data) return '';
  
  const state = lead.data.state || '';
  const city = lead.data.city || '';
  
  if (city && state) {
    return `${city}, ${state}`;
  } else if (city) {
    return city;
  } else if (state) {
    return state;
  }
  
  // Try to get a location field directly
  const location = lead.data.location;
  
  if (location && typeof location === 'string') {
    return location;
  }
  
  return '';
};
